import {React, _, pluralize} from '@api'
import {CREATE_CATALOG_ITEM, ASSIGN_ITEM_TO} from '@api/graphql'
import {Icon} from '@blueprintjs/core'
import {FILTER_UNASSIGNED_ID, SeasonSelectorFilterDropdownStore, SimpleFilterItemDefinition} from '@modules'
import {OFFLINE_ID, OfflineItemWrapper, IUpload, ActionButtonsConfig, IField, ui, CatalogStore} from '@stores'
import {CreationData, IEditableDetails, IMutationObj} from '@stores/elasticsearch/mutations'
import {guid} from '@utils'
import {action, computed, observable, runInAction, toJS} from 'mobx'
import {ConstraintViolations, NoAllowAssignLibraryItemConstraintViolationDialogContent, ReassignLibraryItemDialogContent} from './AssignUniqueLibraryItem'
import * as css from './CatalogCreateItemStore.css'
import {CatalogInstanceChildStore} from './CatalogInstanceChildStore'

const HIERARCHY_FIELDS = ['id', 'exId', 'name']

export class CatalogCreateItemStore extends CatalogInstanceChildStore {
  constructor(public catalog: CatalogStore) {
    super(catalog)
  }

  @observable loader = observable.map<string, { id }>({}, {deep: false})

  @computed get selectedHierarchy() {
    const data: CreationData = {},
      {vlp} = this

    if (vlp?.hierarchy?.hasVlpHierarchy) {
      const {
        hierarchy: {selectedPlans, selectedCollections, selected: {folders}, folderById}
      } = vlp
      selectedPlans?.length == 1 && (data.plan = _.pick(selectedPlans[0], HIERARCHY_FIELDS))
      folders?.length == 1 && (data.subCategories = folders[0].map(id => _.pick(folderById[id], HIERARCHY_FIELDS)))
      selectedCollections?.length === 1 && (data.collection = _.pick(selectedCollections[0], HIERARCHY_FIELDS))
    }
    return data
  }

  @action onCreateItem = async () => {
    return await this.createOfflineItem()
  }

  @action onCreateItems = () => {
    this.catalog.createSlider = true
  }

  onSetAddCatalogItemInputRef = ref => this.addCatalogItemInputRef = ref
  private addCatalogItemInputRef: HTMLInputElement

  @action onCreateItemFromImages = (multiple: boolean = false) => {
    this.multiple = multiple
    this.addCatalogItemInputRef.click()
  }

  onAddItemImageInputChanged = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const {target} = event,
      {catalog: {img: {readFileImages}, createItem: {createOfflineItem}, index}, multiple} = this
    try {
      const images = await readFileImages(target.files)
      if (multiple) {
        images.map(async image => {
          await createOfflineItem({index, data: {images: [image]}})
        })
      } else {
        await createOfflineItem({index, data: {images}})
      }
    } finally {
      this.multiple = false
      this.addCatalogItemInputRef.value = ''
    }
  }

  @observable multiple = false

  @action _duplicateCatalogItemById = async ({index, id, selectedItemData}: { index?: string, id: string, selectedItemData?: any }) => {
    console.debug(`Duplicating ${index}-${id}...`)
    const {catalog, catalog: {stores, offlineItemsByIndex, features: {actionButtons: {duplicate}}}} = this,
      {duplicatedByUser} = duplicate as ActionButtonsConfig,
      addDuplicatedByUserInfo = (d) => {
        if (duplicatedByUser && !_.isEmpty(duplicatedByUser)) {
          console.assert(stores?.users?.current, `User information is missing so will not be able to save it while duplicating record in offline`)
          _.set(d, duplicatedByUser, stores?.users?.current)
        }
      }

    let offlineItem: OfflineItemWrapper = null
    if (!_.isObject(duplicate) || !duplicate.autoPublish) {
      offlineItem = await this.createOfflineItem({index, data: {}, generateName: false, isDuplicate: true})
      // this.loader.set(offlineItem._id, {id: offlineItem._id})
    }

    if (id.startsWith(OFFLINE_ID)) {
      const d = _.cloneDeep(offlineItemsByIndex[index][id])
      delete d.id
      delete d._id
      addDuplicatedByUserInfo(d)
      await this.saveOfflineData({...(offlineItem?.item?.data ?? {}), ...d})
    } else {
      let original = selectedItemData ? selectedItemData : await stores.es.client.get({
          id,
          index: catalog.index
        }),
        {catalog: {catalogConfig: {openInC8Field}, features: {actionButtons: {duplicate}}}} = this

      await runInAction(async () => {
        openInC8Field && _.unset(original, openInC8Field)
        delete original.exId
        const clone = _.merge({}, offlineItem?.item?.data ?? {}, {id: offlineItem?.item?.data?._id, collectionVersion: original?.collectionVersion}, original)
        delete clone.createdAt
        delete clone.updatedAt
        delete clone.exLastSync

        if (!_.isObject(duplicate)) {
          await this.saveOfflineData(clone)
        } else {
          const {onBefore, autoPublish, ignoreFields, sourceUrlField} = duplicate as ActionButtonsConfig

          (ignoreFields ?? []).forEach(f => {
            const pathArr = f.path.split('.')
            if (_.isArray(_.get(clone, pathArr[0]))) {
              _.get(clone, pathArr[0]).map((elem) => {
                _.isUndefined(f?.default) ? _.unset(elem, pathArr.slice(1)) : _.set(elem, pathArr.slice(1), f.default)
              })
            } else {
              f?.default ? _.set(clone, f.path, f.default) : _.unset(clone, f.path)
            }
          })

          if (sourceUrlField && !_.get(clone, sourceUrlField)) {
            _.set(clone, sourceUrlField, id)
          }

          addDuplicatedByUserInfo(clone)

          if (onBefore) {
            const originalBefore = _.cloneDeep(original)
            await onBefore({stores, original, clone})
            if (!_.isEqual(original, originalBefore)) {
              // Original was mutated by before function
              debugger
            }
          }

          if (autoPublish) {
            const newId = await catalog.createItem.createObjectInRemote(clone, true)
            catalog.newlyCreatedIds.push(newId)
          } else {
            await this.saveOfflineData(clone)
          }
        }
      })
    }
    // offlineItem && this.clearLoaderFor(offlineItem._id)
  }

  getGeneratedName = (index?) => {
    const {catalog, catalog: {workspaceByIndex}} = this
    return workspaceByIndex[index ?? catalog.index]?.createDefaultTitle || workspaceByIndex[index ?? catalog.index]?.title
  }

  getDefaultCreateField = (index?: string) => {
    const {catalog, catalog: {workspaceByIndex}} = this
    return workspaceByIndex[index ?? catalog.index]?.createDefaultField || 'name'
  }

  @action createMultipleOfflineItemsByCount = async ({ count, generateName = true }: { count: number, generateName?: boolean }) => {
    const {strings, catalog, catalog: {index, selection, offlineItemsDb, offlineItemsMap}, _assignComputedFieldsValues, _setFilterValuesOnOfflineItems, applyCreateItemDefaults, selectedHierarchy, getDefaultCreateField, getGeneratedName} = this
    const items = Array.from(Array(count).keys())
    const newItems = await Promise.all(items.map(async () => {
      const _id = `${OFFLINE_ID}_${guid()}`,
        _index = index,
        {_nestedValuesForNewItem, defaultValuesForNewItem} = this,
        item = {index: _index, data: {_id, creationDate: new Date(), ...defaultValuesForNewItem(_index), ..._nestedValuesForNewItem(_index), ...selectedHierarchy}}
        generateName && _.set(item.data, getDefaultCreateField(_index), getGeneratedName(_index))
      const computedItem = _assignComputedFieldsValues(item)
      let newItem = await _setFilterValuesOnOfflineItems(computedItem)
      newItem = _.cloneDeep(newItem)
      applyCreateItemDefaults(newItem.data)
      //
      return await this._setFilterValuesOnOfflineItems(newItem)
    }))
    // const offlineItemIds = await Promise.all(newItems.map(async (item) => await offlineItemsDb.offlineItems.add(item)))
    const offlineItemIds = await offlineItemsDb.offlineItems.bulkAdd(newItems, {allKeys: true})
    selection && selection.selectedIds.clear()
    offlineItemIds.forEach((offlineItemId, index) => {
      const newItem = newItems[index]
      runInAction(() => {
        offlineItemsMap[newItem.data._id] = new OfflineItemWrapper(catalog, {id: offlineItemId, ...newItem})
        const {dialogs, catalog: {filteredOfflineItems, catalogItemName, selection, index, filter: {resetFilters}}} = this

        if (newItem.index == index && filteredOfflineItems.some(item => item._id === newItem.data._id)) {
          selection && selection.selectedIds.add(newItem.data._id)
        } else {
          // console.log({newItem, selectedHierarchy})
          dialogs.showToastMessage(
            <div className={css.message}>
              <Icon icon='tick-circle' color='green' iconSize={24}/>
              <span>Your {catalogItemName}(s) has been created, but it is currently hidden due to filters.</span>
              <a onClick={() => resetFilters(true)}>{strings.clearFilters.split(' ').map(_.capitalize).join(' ')}</a>
            </div>
          )
        }
      })
    })

    // items.forEach(item => this.createOfflineItem({index, data: {}}))
  }

  _setFilterValuesOnOfflineItems = async (newItem) => {
    const {es, catalog, catalog: {vlp,
        findFieldInTree, seasonSelectors, seasonSelectorFields, isFieldListType, filter, filter: {filtersWithSelections}}} = this,
      {data} = newItem

    await Promise.all(
      filtersWithSelections.filter(f => f.filterField && !seasonSelectorFields.some(s => s.field === f.group || s.field === f.filterField) && (isFieldListType(findFieldInTree(f.filterField)?.def?.type) || f.state.selected.size === 1)).map(
        async ({group, filterField, state: {selected, groupedByValue}}) => {
          const fieldDef = catalog.findFieldInTree(group) ?? catalog.findFieldInTree(filterField),
            filterValue = isFieldListType(fieldDef?.def?.type) ? Array.from(selected.keys()).map((k: string) => groupedByValue[k]?.id) : groupedByValue[_.first(Array.from(selected.keys()).map(v => v.toString()))]?.id,
            sectionConfig = catalog.sectionConfigFor(fieldDef?.section)

          if (filterValue !== FILTER_UNASSIGNED_ID) {
            if (fieldDef?.esIndex) {
              // Lookup the accompanying item(s) from the library index
              // Note that there can technically be MULTIPLE results returned here as we filter on something like 'colorway.name' which is NOT guaranteed to be unique

              const {items} = es.unwrapHitsResult(
                await es.search({
                  index: fieldDef.esIndex,
                  body: {
                    query: {
                      term: {
                        // colorway.name => [esIndex].name
                        [filterField.substring(filterField.indexOf('.') + 1)]: filterValue
                      }
                    }
                  }
                }))
              if (items.length == 1) {
                _.set(data, fieldDef.field, _.first(items))
              } else {
                // todo - toast this to the user, we don't currently support multiple site message toasts
                console.debug(`Multiple ${pluralize(catalog.labelForField(fieldDef))} were found in the library - cannot determine which to assign.`, items)
              }
            } else {
              if (sectionConfig?.viewOptions?.field) {
                const nestedData = _.get(data, sectionConfig.path),
                  subField = filterField.substring(sectionConfig.path.length + 1)

                if (nestedData) {
                  if (_.isArray(nestedData)) {
                    nestedData.forEach(d => _.set(d, subField, filterValue))
                  } else {
                    _.set(nestedData, subField, filterValue)
                  }
                }
              } else {
                _.set(data, filterField, filterValue)
              }
            }
          }
        }))

    // Set hierarchy fields that are also filters
    // This is extremely complex as we are bound to the C8 merch plan but we also want to allow for maximal flexibility outside of it
    // For example, a collectionVersion (also called a merch season) is thought of as something like FW2022, but
    // in reality every plan -> folder(s) -> collection -> collectionVersion(s) is a unique collection version
    // that all have the same name: 'FW2022'
    _.values(seasonSelectors).forEach(({c8Dict, c8: {services: {instance: c8}},filterGroupStore: {group: {store: {groupName}, selected, groupedByValue, def}}}: SeasonSelectorFilterDropdownStore) => {
        // Set the collectionVersion, etc on the newly created item - ONLY if this isn't being created with the property explicitly set like clicking the + in the unassigned grouping for group by season
       // console.log({groupName, selected, groupedByValue, def})
      const filterValues = [...selected.keys()].map(k => groupedByValue[k])

        let filterValue = _.omit(_.first(filterValues), 'count'),
          seasonValue: SimpleFilterItemDefinition = {..._.pick(filterValue, 'name'),
            id: _.first(filterValue.id),
            exId: _.first(filterValue.id)}

      if (filterValues.length > 1) {
        delete seasonValue.id
        delete seasonValue.exId
      }

      if (groupName === 'collectionVersion') {
        if (data.plan) {  // Creating a new item within a plan group or plan is set in hierarchy
          // Find a collection version without our plan
          const {plan: {id: planId}} = data,
            availableCollectionVersionsFromFilter = filterValues.map(v => v.id.filter(id => vlp.hierarchy.availableCollectionVersionById[id] != null).map(id => c8.collectionVersionById[id])).flat(),
            collectionVersionsInSelectedPlan = availableCollectionVersionsFromFilter.filter(v => v?.plan?.id === data.plan.id)

          if (!_.isEmpty(collectionVersionsInSelectedPlan)) {
            const cv = _.first(collectionVersionsInSelectedPlan)
            // if (collectionVersionsInSelectedPlan.length > 1) {
            //   console.warn(`Using first collection version found in plan when ${collectionVersionsInSelectedPlan.length} are available`, {planId, collectionVersionsInSelectedPlan, cv, filterValues})
            // }
            seasonValue = _.pick(c8Dict[cv.id], [collectionVersionsInSelectedPlan.length > 1 ? null : 'id', 'name'])
            seasonValue.exId = seasonValue.id
          }
          else {
            console.warn(`The season is not available for that plan`)
          }
        }
      }

      const c8Item = c8Dict && seasonValue.id ? c8Dict[seasonValue.id as string] : null

      if (def?.field && seasonValue && !_.isEmpty(seasonValue)) {
          const {type, field} = def
          if (type === 'EnumList') {
            if (seasonValue.id) {
              _.set(data, field, [seasonValue.id])
            }
          } else if (type === 'Enum') {
            if (seasonValue.id) {
              _.set(data, field, seasonValue.id)
            }
          } else {
            _.set(data, field, seasonValue)

            /**
             * VLP items have the following parts:
             *    plan
             *    subcategories
             *    collection
             *    collectionVersion
             *    merchSeason(?)
              */

            if (_.has(c8Item, 'collectionId')) {
              const collection = c8.vlp.hierarchy.collectionById[c8Item['collectionId']]
              if (collection) {
                _.assign(data, _.pick(collection, ['subCategories', 'plan']), {collection: _.pick(collection, ['name', 'id', 'exId'])})
              }
            }
          }
        }
      })

    return newItem
  }

  defaultValuesForNewItem = (index: string) => {
    const {catalog: {stores, fieldByWorkspaceIndex}} = this,
      {fields} = fieldByWorkspaceIndex[index]

    let r = _.values(fields)
      .filter(v => !_.isUndefined(v.defaultValue))
      .reduce((acc, v) => {
        return _.set(acc, v.field, v.defaultValue)
      }, {})

    return r
  }

  _nestedValuesForNewItem = _.memoize((index: string) => {
    const {catalog: {workspaceConfig: {sections}, fieldByWorkspaceIndex, sectionValuesFor, getDefaultValueByType}} = this,
      {fields} = fieldByWorkspaceIndex[index],
      viewOptionSections = (sections ?? []).filter(s => s.viewOptions?.field)
    if (viewOptionSections.length) {
      return viewOptionSections.reduce((acc, section) => {
        const values = sectionValuesFor(section.path),
          customSequence = section.customSequence,
          customSequenceValues = customSequence ? _.sortBy(values, v => customSequence.indexOf(v?.key) >= 0 ? customSequence.indexOf(v?.key) : customSequence.length + 1) : values,
          nestedFields = _.values(fields).filter(f => f.section === section.path)
        _.set(acc, section.path, customSequenceValues.reduce((acc, {key}) => {
          const newItem = {}
          _.set(newItem, section.viewOptions.field, key)
          _.set(newItem, 'exId', key)
          let val: any = ''
          nestedFields.forEach(field => {
            const sectionField = field.field.replace(`${section.path}.`, '')
            if (sectionField !== section.viewOptions.field) {
              val = getDefaultValueByType(field.def?.type)
              _.set(newItem, sectionField, val)
            }
          })
          return [...acc, newItem]
        }, []))
        return acc
      }, [])
    }
    return []
  })

  @action _assignComputedFieldsValues = (item) => {
    const {catalog: {computedFields, getDefaultValueByType}} = this,
      {data} = item
    if (_.size(computedFields)) {
      let val: any = ''
      computedFields.forEach(field => {
        val = getDefaultValueByType(field.def?.type) ?? 0
        _.set(data, field.field, val)
      })
    }
    return item
  }

  @action createOfflineItem = async ({index, data, generateName = true, isDuplicate = false}: { index?: string, data?: any, generateName?: boolean, isDuplicate?: boolean } = {}) => {
    const _id = `${OFFLINE_ID}_${guid()}`,
      {
        strings, catalog, catalog: {offlineItemsDb, offlineItemsMap, catalogItemName, selection: {selectedIds}, img: {updateOfflineDataImages}, filter: {resetFilters}},
        selectedHierarchy, getDefaultCreateField, _setFilterValuesOnOfflineItems, getGeneratedName, defaultValuesForNewItem, _nestedValuesForNewItem, _assignComputedFieldsValues, applyCreateItemDefaults
      } = this,
      _index = index ?? catalog.index
    let item = {index: _index, data: {_id, creationDate: new Date(), ...defaultValuesForNewItem(_index), ..._nestedValuesForNewItem(_index), ...selectedHierarchy}}

    const _data = await updateOfflineDataImages(data)

    if (!_.isEmpty(data)) {
      item = _.merge(item, {data: {...data, ..._data}})
    }

    generateName && _.set(item.data, getDefaultCreateField(_index), getGeneratedName(_index))
    const computedItem = !isDuplicate && _assignComputedFieldsValues(item) || item
    let newItem = await _setFilterValuesOnOfflineItems(computedItem)
    newItem = _.cloneDeep(newItem)
    applyCreateItemDefaults(newItem.data)

    const offlineItemId = await offlineItemsDb.offlineItems.add(newItem)
    runInAction(() => {
      const offlineItem = offlineItemsMap[_id] = new OfflineItemWrapper(catalog, {id: offlineItemId, ...newItem})

      // Wait to lookup filtered items until we've added above
      const {catalog: {toDispose, dialogs, filteredOfflineItems}} = this
      toDispose.push(offlineItem.autorun_watchForChanges())

      // console.log(`Created new Pending Item`, {newItem, selectedHierarchy})

      if (newItem.index == catalog.index && filteredOfflineItems.some(item => item._id === _id)) {
        if (catalog.isProductView) {
          selectedIds.clear()
          selectedIds.add(_id)
        }
      } else {
        dialogs.showToastMessage(
          <div className={css.message}>
            <Icon icon='tick-circle' color='green' iconSize={24}/>
            <span>Your {catalogItemName} has been created, but it is currently hidden due to filters.</span>
            <a onClick={() => resetFilters(true)}>{strings.clearFilters.split(' ').map(_.capitalize).join(' ')}</a>
          </div>
        )
      }
    })

    return catalog.offlineItemsMap[_id]

    //right.updateState({open: true, path: [{sidebarContext: 'create-catalog-item', data}]})
    //this.offlineItems.offlineItems.add({index: catalog.index, })
  }

  // constructor(public details?: CatalogItemSidebarDetailsStore) {
  //   details && setTimeout(() => {
  //     autorun(() => {
  //       const {details: { modal: {dropDownStore: {selected}}}} = this
  //       runInAction(() => {
  //         selected.forEach(item => {
  //           Object.entries(item).forEach(([key, value]) => {
  //             const val = value[0]
  //             const d = { ...this.data }
  //             if (val) {
  //               d[key] = { exId: val.id, ...val }
  //             } else {
  //               delete d[key]
  //             }
  //             this.data = d
  //             // const itemValue = value[0]
  //             // itemValue //_.set(this.data, [key], { [key]: { exId: itemValue.id, ...itemValue }, [`${key}_changed`]: true })
  //           })
  //         })
  //       })
  //     })
  //   }, 0)
  // }

  /**
   *
   *  fieldObj
   * updatedValueItem
   */
    // mutateField = ({fieldObj, newValue}: { fieldObj: DetailsViewFieldConfig, newValue: any }) => {
    //   const {def: type} = _.isEmpty(fieldObj?.def) ? catalog._fieldByName[fieldObj.field] : fieldObj
    //   if (!fieldObj) {
    //     console.error(`Unable to find field ${fieldObj.field}`)
    //   }
    //
    //   const {field} = fieldObj
    //
    //   if (!field) throw new Error(`field property is not defined on ${stringify(fieldObj)}`)
    //   _.setWith(this.data, field, toJS(newValue), Object)
    // }

  @action applyCreateItemDefaults = (data) => {
    const {catalog: {itemCreationConfig, stores}} = this
    if (!itemCreationConfig) return

    const {copyOverFields, defaultFieldValues} = itemCreationConfig
    if (!_.isEmpty(copyOverFields)) {
      copyOverFields.forEach(f => {
        if (_.isEmpty(_.get(data, f?.target?.split('.'), null))) {
          _.set(data, f?.target?.split('.'), _.get(data, f?.source?.split('.')))
        }
      })
    }

    if (!_.isEmpty(defaultFieldValues)) {
      defaultFieldValues.forEach((field: IField) => {
        if (field?.field) {
          const value = _.isFunction(field.value) ? field.value(stores) : field.value
          _.set(data, field?.field?.split('.'), value)
        }
      })
    }
  }

  @action saveOfflineData = async (data: any) => {
    {
      this.applyCreateItemDefaults(data)

      // console.log(`saveOfflineData`, data)
      const {catalog} = this
      return await catalog.upsertOfflineItem({index: catalog.index, value: {...data, id: data._id}})
    }
  }

  _setDataForRelatedValue = async ({relatedField, relatedProperty, relatedValue, changes}) => {
    const {es} = this
    const {items} = es.unwrapHitsResult(await es.search({
      index: relatedField.esIndex,
      body: {
        query: {
          match: {
            [relatedProperty]: relatedValue
          }
        }
      }
    }))

    if (items.length != 1) {
      throw new Error(`Unable to find matching constrained item in ${relatedField.esIndex} where ${relatedProperty} == ${relatedValue}`)
    } else {
      _.set(changes, relatedField.field, {field: relatedField.field, updatedValue: items[0]})
    }
    return changes
  }

  @action private _enforceUniqueConstraintsDuringAssign = async (e: IEditableDetails & { clearLoader?: boolean, query?: string, queryItemIds: Array<string> }) => {
    const {strings, es, catalog, catalog: {selectedImageSourceFields}, workspaces, dialogs} = this
    const {changes, queryItemIds, toIndex, idField} = e,
      mutationsWithUniqueConstraints = _.values(changes).filter(({updatedValue, field}) => {
        if (updatedValue) {
          const library = workspaces.librariesOfCurrentWorkspace.find(l => l.assignTo === field || l?.assignTo2?.field === field)
          return library?.assignUnique || library?.assignTo2?.unique
        }

        return false
      })

    if (mutationsWithUniqueConstraints.length && queryItemIds.length != 1) {
      throw new Error(`Unique fields cannot be assigned to multiple objects`)
    }

    /**
     * Verify uniqueness constraints (colorway can only be assigned to one MPV in the board)
     */
    if (mutationsWithUniqueConstraints.length) {
      const toId = _.first(queryItemIds),
        catalogItem = await es.get({index: toIndex, id: toId})

      const r = await Promise.all(mutationsWithUniqueConstraints.map(async (value: IMutationObj) => {
        const {field, updatedValue} = value,
          fieldDef = catalog._fieldByName[field],
          library = workspaces.librariesOfCurrentWorkspace.find(l => l.assignTo === field || l?.assignTo2?.field === field)

        //console.log({library, updatedValue})

        // Verify constraints if they exist (for example, having a matching colorway or attributes where this style is already assigned to in UR)
        if (library?.assignTo2) {
          const {assignTo2: {field, constraints}} = library,
            libraryItem = await es.get({index: library.index, id: updatedValue.exId})

          // console.log(updatedValue)
          const constraintResults: any = _.flatMap(
            (await Promise.all(
              constraints?.map(async (c) => {
                console.log({field, updatedValue})
                const {catalogItemsTerm, matchingFields} = c,
                  {items: otherCatalogItems} = es.unwrapHitsResult(
                    await es.search({
                      meta: `Unique constraint search for library ${library.name} assignment to catalog item`,
                      index: toIndex,
                      body: {
                        query: {
                          ...catalogItemsTerm(libraryItem)
                        },
                        _source: _.uniq([...matchingFields, ...(selectedImageSourceFields ?? []), catalog.dataMap.title, catalog.dataMap.subtitle1].filter(_.identity))
                      }
                    }, {log: {query: false}}))

                return this._filterOtherCatalogItems({
                  items: otherCatalogItems,
                  matchingFields,
                  constraint: c,
                  catalogItem
                })
              }))))

          if (constraintResults.length) {
            console.warn(`Cannot assign - violation of matching fields constraint`, constraintResults)
            catalog.dialogs.confirmationPopup = {
              show: true,
              onConfirm: () => { },
              confirmationLabel: strings.ok,
              canCancel: false,
              children: <NoAllowAssignLibraryItemConstraintViolationDialogContent library={library} libraryItem={updatedValue} catalogItem={catalogItem} violations={constraintResults}/>

            }
            return false
          }
        }

        // Make sure this value isn't already assigned elsewhere (uniqueness violation)

        // Make sure we're not overwriting any already assigned fields, if so prompt the user
        const currentAssignedValue = _.get(catalogItem, field)

        const {items: alreadyAssignedTo} = es.unwrapHitsResult(await es.search({
          index: catalog.index,
          body: {
            query: {
              match: {
                [`${field}.exId`]: updatedValue.exId
              }
            }
          }
        }, {log: {query: false}}))

        if (alreadyAssignedTo.length > 0 || currentAssignedValue) {
          console.log(`Uniqueness violation for field ${field}`, toJS({catalogItem, alreadyAssignedTo, currentAssignedValue, updatedValue}, {recurseEverything: true}))

          if (currentAssignedValue?.id == updatedValue?.id) {
            //site.message =
            dialogs.showToastMessage(strings.valueAlreadyAssigned)
            return false
          }
          dialogs.confirmationPopup = {
            show: true,
            canCancel: alreadyAssignedTo.length == 0,
            confirmationLabel: alreadyAssignedTo.length > 0 ? strings.ok : 'Replace',
            children: <ReassignLibraryItemDialogContent library={library}
                                                        newValue={updatedValue}
                                                        catalogItem={catalogItem}
                                                        alreadyAssignedTo={alreadyAssignedTo}
                                                        currentAssignedValue={currentAssignedValue}
            />,
            onConfirm: async () => {
              if (alreadyAssignedTo.length) return

              // Unassign
              await Promise.all(
                alreadyAssignedTo.map(async () => {
                  // if (fieldDef.constraint) {
                  // Should we also unassign the style when reassigning?
                  // }

                  let mutations = {
                    [field]: {field, updatedValue: null}
                  }

                  if (fieldDef.constraint) {
                    const {relatedField} = catalog.findConstraintRelatedField(fieldDef)
                    _.set(mutations, relatedField.field, {field: relatedField.field, updatedValue: null})
                  }
                  await this.updateObjectInRemote({
                    toIndex,
                    changes: mutations
                  }, true)
                }))

              await this.updateObjectInRemote({toId, toIndex, changes: changes}, true)
              catalog.refetch('catalogItems')
            }
          }

          return false
        }
      }))

      // Do we have to prompt the user or did we violate a constraint?  If so, fail
      if (r.some(v => v === false)) {
        return false
      }
    }

    // Verify any other constraints such as style must match colorway or certain attribute rules (can be custom logic in config)
    await Promise.all(
      _.map(changes, async (value: IMutationObj) => {
        const {field, updatedValue, baseNode} = value,
          fieldDef = catalog._fieldByName[field] ?? catalog._fieldByName[baseNode] ?? catalog._fieldByName[idField]

        if (fieldDef?.constraint) {
          // Lookup the constrained field and update accordingly

          const {relatedField, relatedProperty} = catalog.findConstraintRelatedField(fieldDef)

          //console.log({relatedField, relatedProperty})
          if (relatedField) {
            const relatedValue = _.get(changes[field].updatedValue, fieldDef.constraint.to)
            if (relatedValue) {
              return this._setDataForRelatedValue({
                relatedField,
                relatedProperty,
                relatedValue,
                changes
              })
              /*const {items} = es.unwrapHitsResult(await es.search({
                index: relatedField.esIndex,
                body: {
                  query: {
                    match: {
                      [relatedProperty]: relatedValue
                    }
                  }
                }
              }))

              if (items.length != 1) {
                throw new Error(`Unable to find matching constrained item in ${relatedField.esIndex} where ${relatedProperty} == ${relatedValue}`)
              } else {
                _.set(changes, relatedField.field, {field: relatedField.field, updatedValue: items[0]})
              }*/
            } else {
              console.warn(`Unable to find related value at path ${fieldDef?.constraint?.to} in `, changes)
            }
          }
        }

        if (fieldDef?.onAssign) {
          return await fieldDef.onAssign({field: fieldDef, mutations: changes, libraryItem: updatedValue, stores: this.catalog.stores})
        }
      }))

    return true
  }

  _filterOtherCatalogItems = ({items, matchingFields, catalogItem, constraint}) => {
    return items.map(item => ({
      constraint,
      item,
      nonMatching: matchingFields.map(f => {
        const itemValue = _.get(item, f), assignToValue = _.get(catalogItem, f)
        //console.log({f, self, other})
        return itemValue !== assignToValue ? {field: f, itemValue, assignToValue} : null
      }).filter(_.identity)
    })).filter(v => v.nonMatching.length > 0)
  }

  @action updateObjectInRemoteByQuery = async (e: IEditableDetails & { clearLoader?: boolean, query?: string, queryItemIds: Array<string>, isMultiple?: boolean, data?: any, candidateOnly?: boolean, onCancel?: () => void }, force = false) => {
    // console.trace(toJS(e, {recurseEverything: true}))
    const {strings, workspaces, es, catalog, stores} = this,
      {filter, dialogs} = catalog, {toIndex, query, changes: mutations, clearLoader, queryItemIds, idField, data, candidateOnly, onCancel} = e
    // if toId is present, its a single mutation operation
    if (toIndex == catalog.index) {
      if (!force) {
        // If we're assigning to a catalog item field which is a library with uniqueness requirements between sibling library items (UR) then verify the associated library item (a style for a given colorway for UR) meets our constraints
        // YFTS-6351, YFTS-6393
        const r = await Promise.all(
          _.map(mutations, async (value: IMutationObj) => {
            const {field, updatedValue} = value,
              fieldDef = catalog._fieldByName[field],
              library = workspaces.librariesOfCurrentWorkspace.find(l => l.assignTo === field || l?.assignTo2?.field === field)

            //console.log({library, updatedValue})

            if (!force // User hasn't already clicked OK in a dialog to overwrite
                && updatedValue // user isn't unassigning/nulling the field
                && (library?.assignUnique || library?.assignTo2?.unique)) {
              const catalogId = _.first(queryItemIds)
              // Make sure we're not overwriting any already assigned fields, if so prompt the user
              const catalogItem = catalog.catalogItemById[catalogId],
                currentAssignedValue = _.get(catalogItem, field)

              const {items: alreadyAssignedTo} = es.unwrapHitsResult(await es.search({
                index: catalog.index,
                body: {
                  query: {
                    match: {
                      [`${field}.id`]: updatedValue.id
                    }
                  }
                }
              }, {log: {query: false}}))

              if (alreadyAssignedTo.length > 0) {
                if (currentAssignedValue?.id == updatedValue?.id) {
                  //site.message =
                  dialogs.showToastMessage(strings.valueAlreadyAssigned)
                  return false
                }
                dialogs.confirmationPopup = {
                  show: true,
                  canCancel: false,
                  confirmationLabel: strings.ok,
                  children: <ReassignLibraryItemDialogContent library={library}
                                                              newValue={updatedValue}
                                                              catalogItem={catalogItem}
                                                              alreadyAssignedTo={alreadyAssignedTo}
                  />
                }

                return false
              }
              let currentAssignRelatedField = true
              if (!_.isEmpty(library?.assignTo2?.constraints)) {
                const {assignTo2: {field, constraints}} = library, {selectedImageSourceFields} = catalog
                console.log({toIndex, catalogId})

                // Lookup the full catalog item - we need to compare various fields in UR to determine if it's ok to assign
                const catalogItem = await es.get({index: toIndex, id: catalogId})
                let nonMatchRelatedField = false
                // console.log(updatedValue)

                // Lookup other catalog items (in UR case we need to find MPVs with the same style already assigned) and verify all required fields match
                const nonMatchingCatalogItems: Array<ConstraintViolations> = _.flatMap(
                  (await Promise.all(
                    constraints?.map(async (c) => {
                      const {catalogItemsTerm, matchingFields} = c,
                        {items: otherCatalogItems} = es.unwrapHitsResult(
                          await es.search({
                            meta: `Unique constraint search for library ${library.name} assignment to catalog item`,
                            index: toIndex,
                            body: {
                              query: {
                                ...catalogItemsTerm(updatedValue)
                              },
                              // Get all the fields needed to match on as well as fields for displaying on the card in the overwrite confirmation dialog
                              _source: _.uniq([...matchingFields, ...(selectedImageSourceFields ?? []), catalog.dataMap.title, catalog.dataMap.subtitle1].filter(_.identity))
                            }
                          }, {log: {query: false}}))

                      // Do any of the already assigned catalog items not have matching required fields?
                      return this._filterOtherCatalogItems({
                        items: otherCatalogItems,
                        matchingFields,
                        constraint: c,
                        catalogItem
                      })
                    }))))

                console.log({catalogItem, library, value, nonMatchingCatalogItems})
                if (!nonMatchingCatalogItems.length) {
                  if (fieldDef?.constraint) {
                    const {relatedField, relatedProperty} = catalog.findConstraintRelatedField(fieldDef),
                      catalogRelatedValue = _.get(catalogItem, `${relatedField.field}.${relatedProperty}`),
                      catalogValue = _.get(catalogItem, field),
                      updatingValue = _.isObject(updatedValue) ? _.get(updatedValue, `${relatedField.field}.${relatedProperty}`) : undefined

                    nonMatchRelatedField = _.isEmpty(catalogValue) && catalogRelatedValue !== updatingValue && updatingValue !== undefined && catalogRelatedValue !== undefined
                    currentAssignRelatedField = !(!_.isEmpty(catalogValue) && catalogRelatedValue === updatingValue)
                  }
                }
                if (nonMatchingCatalogItems.length || nonMatchRelatedField) {
                  console.warn(`Cannot assign - violation of matching fields constraint`, nonMatchingCatalogItems)
                  dialogs.confirmationPopup = {
                    show: true,
                    onConfirm: () => {},
                    confirmationLabel: strings.ok,
                    canCancel: false,
                    children: <NoAllowAssignLibraryItemConstraintViolationDialogContent library={library} libraryItem={updatedValue} catalogItem={catalogItem} violations={nonMatchingCatalogItems}/>

                  }
                  return false
                }
              }

              if (currentAssignedValue && currentAssignRelatedField) {
                dialogs.confirmationPopup = {
                  show: true,
                  canCancel: true,
                  operation: 'REPLACE_PRODUCT',
                  children: <ReassignLibraryItemDialogContent library={library}
                                                              newValue={updatedValue}
                                                              catalogItem={catalogItem}
                                                              alreadyAssignedTo={alreadyAssignedTo}
                                                              currentAssignedValue={currentAssignedValue}
                  />,
                  onCancel: () => {
                    onCancel && onCancel()
                  },
                  onConfirm: async () => {
                    // Unassign
                    await Promise.all(
                      alreadyAssignedTo.map(async (item) => {
                        // if (fieldDef.constraint) {
                        // Should we also unassign the style when reassigning?
                        // }

                        let mutations = {
                          [field]: {field, updatedValue: null}
                        }

                        if (fieldDef.constraint) {
                          const {relatedField} = catalog.findConstraintRelatedField(fieldDef)
                          _.set(mutations, relatedField.field, {field: relatedField.field, updatedValue: null})
                        }
                        await this.updateObjectInRemote({
                          toId: item._id,
                          toIndex,
                          changes: mutations
                        }, true)
                      }))

                    if (fieldDef.constraint) {
                      const {relatedField} = catalog.findConstraintRelatedField(fieldDef)
                      _.set(mutations, relatedField.field, {field: relatedField.field, updatedValue: null})
                    }

                    const r = await this.updateObjectInRemote({toId: catalogId, toIndex, changes: mutations}, true)
                    if (r === false) {
                      return false
                    }
                    r?.data?.res?.message && dialogs.showSuccessMessage(r?.data?.res?.message)
                    catalog.refetch('catalogItems')
                  }
                }

                return null
              }
            }

            return true
          }))

        if (r.some(v => v === false)) {
          return false
        }

        if (r.some(v => v === null)) {
          return null
        }
      }
      await Promise.all(
        _.map(mutations, async (value: IMutationObj) => {
          const {field, updatedValue, baseNode} = value,
            fieldDef = catalog._fieldByName[field] ?? catalog._fieldByName[baseNode] ?? catalog._fieldByName[idField]

          if (fieldDef?.constraint) {
            // Lookup the constrained field and update accordingly

            const {relatedField, relatedProperty} = catalog.findConstraintRelatedField(fieldDef)

            //console.log({relatedField, relatedProperty})
            if (relatedField) {
              const relatedValue = _.get(mutations[field].updatedValue, fieldDef.constraint.to)
              if (relatedValue) {
                return this._setDataForRelatedValue({
                  relatedField,
                  relatedProperty,
                  relatedValue,
                  changes: mutations
                })
                /*const {items} = es.unwrapHitsResult(await es.search({
                  index: relatedField.esIndex,
                  body: {
                    query: {
                      match: {
                        [relatedProperty]: relatedValue
                      }
                    }
                  }
                }))

                if (items.length != 1) {
                  throw new Error(`Unable to find matching constrained item in ${relatedField.esIndex} where ${relatedProperty} == ${relatedValue}`)
                } else {
                  _.set(mutations, relatedField.field, {field: relatedField.field, updatedValue: items[0]})
                }*/
              } else {
                console.warn(`Unable to find related value at path ${fieldDef.constraint.to} in `, mutations)
              }
            }
          }

          if (fieldDef?.onAssign) {
            await fieldDef.onAssign({field: fieldDef, mutations, libraryItem: updatedValue, stores, data, candidateOnly})
          }
        }))
    }

    //TODO-set Loader via fetching
    if (!_.isEmpty(queryItemIds)) {queryItemIds.map((toId) => this.loader.set(toId, {id: toId}))}
    let updatedObj = {
      // exId: toId
    }

    // console.log({mutations})
    _.forEach(mutations, (mutation: IMutationObj) => {
      const mutationField = mutation.field

      //START: To handle drag n drop specification
      let fieldByName = catalog?._fieldByName[mutationField]
      const firstStringField = mutationField.split('.')[0],
        rootField = catalog?._fieldByName[firstStringField],
        tailField = catalog?._fieldByName[mutationField]
      if (rootField?.esIndex && mutationField.split('.')?.length == 1) {
        fieldByName = rootField
        updatedObj[firstStringField] = {updatedValue: mutation.updatedValue, fieldType: mutation?.type?.type ?? fieldByName?.def?.type, where: mutation?.where ?? null}
      } else if (tailField?.esIndex) {
        fieldByName = tailField
        updatedObj[mutationField] = {updatedValue: mutation.updatedValue, fieldType: mutation?.type?.type ?? fieldByName?.def?.type, where: mutation?.where ?? null}
        //END: To handle drag n drop specification
      } else {
        updatedObj[mutation.field] = {updatedValue: mutation.updatedValue, fieldType: mutation?.type?.type ?? fieldByName?.def?.type, where: mutation?.where ?? null}
      }
    })
    // console.log({updatedObj})
    const changes = _.map(updatedObj, ({updatedValue, fieldType, where}, field) => ({field, value: updatedValue, fieldType: fieldType, where}))

    const variables = {
      input: {
        index: toIndex,
        query,
        changes
      }
    }
    // console.log(toJS({variables}, {recurseEverything: true}))

    const res = await stores.apollo.client.mutate({
      mutation: ASSIGN_ITEM_TO,
      variables: variables
    })
    // TODO-if syncOnSave is true, sync by query instead of one-by-one id syncing
    if (catalog.catalogConfig?.features?.syncOnSave && queryItemIds.length) {
      console.warn(`NYI - Saving to multiple catalog items only will cause first item to sync`)
      await stores.syncProgress.onStartSync({itemId: _.first(queryItemIds), syncId: catalog.index})
    }

    // TODO: clearing loader logic changes ?
    // Clear loaders for any item IDs passed in
    if ((clearLoader === true || typeof clearLoader === 'undefined') && !_.isEmpty(queryItemIds)) {
      queryItemIds.forEach(this.clearLoaderFor)
    }

    // YFTS-6083 - After assigning a library item to a catalog item, we need to refresh the library items list if we are filtering on unassigned items
    await Promise.all(_.map(mutations, async (value: IMutationObj) => {
      const library = workspaces.libraryByIndex[catalog._fieldByName[value.field]?.esIndex]
      if (library) {
        const libraryFilterStore = filter.storeByLibraryId.get(library.name)
        if (libraryFilterStore && libraryFilterStore.unassignedOnly) {
          await Promise.all([libraryFilterStore.refetch()])
          catalog.refetch(library.name)
        }
      }
    }))

    // TODO check the API output and hook it to status directly
    return res.data ? {data: {res: {updated: true, message: res?.data?.assignTo?.message}}, status: 200} : {res, status: 204}
  }

  // Being used as a replacement for es.mutate call which pass on the editable fields data for mutation
  @action updateObjectInRemote = async ({toIndex, toId, docMapping, idField, changes, clearLoader, isMultiple = false, data, candidateOnly, onCancel}: IEditableDetails & { toId?: string, clearLoader?: boolean, isMultiple?: boolean, data?: any, candidateOnly?: boolean, onCancel?: () => void }, force = false) => {
    // console.trace({toIndex, toId, docMapping, idField}, toJS(mutations, {recurseEverything: true}))
    const {strings, dialogs, workspaces, es, catalog, stores} = this
    if (ui.settings.usingBulkAssignAPI || true) {
      const res = await this.updateObjectInRemoteByQuery({toIndex, query: `{"match": {"exId": "${toId}"}}`, queryItemIds: [toId], changes: changes, clearLoader, idField, isMultiple, data, candidateOnly, onCancel}, force)
      catalog.updateTableViewData({index: toIndex, ids: [toId]})
      return res
    }
    if (toIndex == catalog.index) {
      if (!force) {
        // If we're assigning to a catalog item field which is a library with uniqueness requirements between sibling library items (UR) then verify the associated library item (a style for a given colorway for UR) meets our constraints
        // YFTS-6351, YFTS-6393
        const r = await Promise.all(
          _.map(changes, async (value: IMutationObj) => {
            const {field, updatedValue} = value,
              fieldDef = catalog._fieldByName[field],

              library = workspaces.librariesOfCurrentWorkspace.find(l => l.assignTo === field || l?.assignTo2?.field === field)

            //console.log({library, updatedValue})

            if (!force // User hasn't already clicked OK in a dialog to overwrite
                && catalog.settings.autoSaveInlineChanges
                && updatedValue // user isn't unassigning/nulling the field
                && (library?.assignUnique || library?.assignTo2?.unique)) {

              // Make sure we're not overwriting any already assigned fields, if so prompt the user
              const catalogItem = catalog.catalogItemById[toId],
                currentAssignedValue = _.get(catalogItem, field)

              const {items: alreadyAssignedTo} = es.unwrapHitsResult(await es.search({
                index: catalog.index,
                body: {
                  query: {
                    match: {
                      [`${field}.id`]: updatedValue.id
                    }
                  }
                }
              }, {log: {query: false}}))

              if (alreadyAssignedTo.length > 0) {
                if (currentAssignedValue?.id == updatedValue?.id) {
                  //site.message =
                  dialogs.showToastMessage(strings.valueAlreadyAssigned)
                  return false
                }
                dialogs.confirmationPopup = {
                  show: true,
                  canCancel: false,
                  confirmationLabel: strings.ok,
                  children: <ReassignLibraryItemDialogContent library={library}
                                                              newValue={updatedValue}
                                                              catalogItem={catalogItem}
                                                              alreadyAssignedTo={alreadyAssignedTo}
                  />
                }

                return false
              }
              let currentAssignRelatedField = true
              if (!_.isEmpty(library?.assignTo2?.constraints)) {
                const {assignTo2: {field, constraints}} = library, {selectedImageSourceFields} = catalog
                console.log({toIndex, toId})

                // Lookup the full catalog item - we need to compare various fields in UR to determine if it's ok to assign
                const catalogItem = await es.get({index: toIndex, id: toId})
                let nonMatchRelatedField = false
                // console.log(updatedValue)

                // Lookup other catalog items (in UR case we need to find MPVs with the same style already assigned) and verify all required fields match
                const nonMatchingCatalogItems: Array<ConstraintViolations> = _.flatMap(
                  (await Promise.all(
                    constraints?.map(async (c) => {
                      const {catalogItemsTerm, matchingFields} = c,
                        {items: otherCatalogItems} = es.unwrapHitsResult(
                          await es.search({
                            meta: `Unique constraint search for library ${library.name} assignment to catalog item`,
                            index: toIndex,
                            body: {
                              query: {
                                ...catalogItemsTerm(updatedValue)
                              },
                              // Get all the fields needed to match on as well as fields for displaying on the card in the overwrite confirmation dialog
                              _source: _.uniq([...matchingFields, ...(selectedImageSourceFields ?? []), catalog.dataMap.title, catalog.dataMap.subtitle1].filter(_.identity))
                            }
                          }, {log: {query: false}}))

                      // Do any of the already assigned catalog items not have matching required fields?
                      return this._filterOtherCatalogItems({
                        items: otherCatalogItems,
                        constraint: c,
                        matchingFields,
                        catalogItem
                      })
                    }))))

                console.log({catalogItem, library, value, nonMatchingCatalogItems})
                if (!nonMatchingCatalogItems.length) {
                  if (fieldDef?.constraint) {
                    const {relatedField, relatedProperty} = catalog.findConstraintRelatedField(fieldDef),
                      catalogRelatedValue = _.get(catalogItem, `${relatedField.field}.${relatedProperty}`),
                      catalogValue = _.get(catalogItem, field),
                      updatingValue = _.isObject(updatedValue) ? _.get(updatedValue, `${relatedField.field}.${relatedProperty}`) : undefined

                    nonMatchRelatedField = _.isEmpty(catalogValue) && catalogRelatedValue !== updatingValue && updatingValue !== undefined && catalogRelatedValue !== undefined
                    currentAssignRelatedField = !(!_.isEmpty(catalogValue) && catalogRelatedValue === updatingValue)
                  }
                }
                if (nonMatchingCatalogItems.length || nonMatchRelatedField) {
                  console.warn(`Cannot assign - violation of matching fields constraint`, nonMatchingCatalogItems)
                  dialogs.confirmationPopup = {
                    show: true,
                    onConfirm: () => {},
                    confirmationLabel: strings.ok,
                    canCancel: false,
                    children: <NoAllowAssignLibraryItemConstraintViolationDialogContent library={library} libraryItem={updatedValue} catalogItem={catalogItem} violations={nonMatchingCatalogItems}/>

                  }
                  return false
                }
              }

              if (currentAssignedValue && currentAssignRelatedField) {
                dialogs.confirmationPopup = {
                  show: true,
                  canCancel: true,
                  operation: 'REPLACE_PRODUCT',
                  children: <ReassignLibraryItemDialogContent library={library}
                                                              newValue={updatedValue}
                                                              catalogItem={catalogItem}
                                                              alreadyAssignedTo={alreadyAssignedTo}
                                                              currentAssignedValue={currentAssignedValue}
                  />,
                  onCancel: () => {
                    onCancel && onCancel()
                  },
                  onConfirm: async () => {
                    // Unassign
                    await Promise.all(
                      alreadyAssignedTo.map(async (item) => {
                        // if (fieldDef.constraint) {
                        // Should we also unassign the style when reassigning?
                        // }

                        let mutations = {
                          [field]: {field, updatedValue: null}
                        }

                        if (fieldDef.constraint) {
                          const {relatedField} = catalog.findConstraintRelatedField(fieldDef)
                          _.set(mutations, relatedField.field, {field: relatedField.field, updatedValue: null})
                        }
                        await this.updateObjectInRemote({
                          toId: item._id,
                          toIndex,
                          changes: mutations
                        }, true)
                      }))

                    if (fieldDef.constraint) {
                      const {relatedField} = catalog.findConstraintRelatedField(fieldDef)
                      _.set(changes, relatedField.field, {field: relatedField.field, updatedValue: null})
                    }

                    const r = await this.updateObjectInRemote({toId, toIndex, changes: changes}, true)
                    if (r === false) {
                      return false
                    }
                    r?.data?.res?.message && dialogs.showSuccessMessage(r?.data?.res?.message)
                    catalog.refetch('catalogItems')
                  }
                }

                return null
              }
            }

            return true
          }))

        if (r.some(v => v === false)) {
          return false
        }

        if (r.some(v => v === null)) {
          return null
        }
      }

      await Promise.all(
        _.map(changes, async (value: IMutationObj) => {
          const {field, updatedValue, baseNode} = value,
            fieldDef = catalog._fieldByName[field] ?? catalog._fieldByName[baseNode] ?? catalog._fieldByName[idField]

          if (fieldDef?.constraint && fieldDef.constraint?.from !== fieldDef.constraint?.to) {
            // Lookup the constrained field and update accordingly

            const {relatedField, relatedProperty} = catalog.findConstraintRelatedField(fieldDef)

            //console.log({relatedField, relatedProperty})
            if (relatedField) {
              const relatedValue = _.get(changes[field].updatedValue, fieldDef.constraint.to)
              if (relatedValue) {
                return this._setDataForRelatedValue({
                  relatedField,
                  relatedProperty,
                  relatedValue,
                  changes
                })
              } else {
                console.warn(`Unable to find related value at path ${fieldDef.constraint.to} in `, JSON.stringify(changes))
              }
            }
          }

          if (fieldDef?.onAssign) {
            const catalogItem = catalog.catalogItemById[data.exId] || data
            await fieldDef.onAssign({field: fieldDef, mutations: changes, libraryItem: updatedValue, stores, data: catalogItem, candidateOnly})
          }
        }))
    }

    this.loader.set(toId, {id: toId})
    let updatedObj = {
      exId: toId
    }
    if (!_.isEmpty(changes)) {
      _.forEach(changes, (value: IMutationObj) => {
        const valueField = value.field.startsWith('secondaries') ? 'secondaries' : value.field,
          {libraryById} = catalog,
          childValueField = valueField?.split('.')[0]
        //START: To handle drag n drop specification
        let fieldByName
        try {
          if (childValueField && libraryById[childValueField])
            fieldByName = catalog?.fieldByName(valueField.split('.')[0])
        } catch (e) {
          console.log(e)
        }
        if (fieldByName?.esIndex) {
          _.set(updatedObj, valueField.split('.')[0], value.updatedValue)
          //END: To handle drag n drop specification
        } else {
          _.set(updatedObj, valueField, value.updatedValue)
        }
      })
      const variables = {
        input: {
          index: toIndex,
          data: updatedObj
        }
      }
      //console.log(variables)

      const res = await stores.apollo.client.mutate({
        mutation: CREATE_CATALOG_ITEM,
        variables: variables
      })

      if (catalog.catalogConfig?.features?.syncOnSave) {
        stores.syncProgress.onStartSync(toId, catalog.index)
      }

      if (clearLoader !== false) { this.clearLoaderFor(toId) }

      // YFTS-6083 - After assigning a library item to a catalog item, we need to refresh the library items list if we are filtering on unasisgned items
      if (!isMultiple) {
        _.forEach(changes, (value: IMutationObj) => {
          const field = catalog._fieldByName[value.field]
          if (field?.esIndex) {
            const library = workspaces.libraryByIndex[field.esIndex]
            if (library) {
              const libraryFilterStore = catalog.filter.storeByLibraryId.get(library.name)
              if (libraryFilterStore && libraryFilterStore.unassignedOnly) {
                libraryFilterStore.refetch()
              }
              catalog.refetch(library.name)
            }
          }
        })
      }

      // TODO check the API output and hook it to status directly
      return res.data ? {data: {res: {updated: true, message: res?.data?.createCatalogItem?.message}}, status: 200} : {res, status: 204}
    }
    if (clearLoader !== false) { this.clearLoaderFor(toId) }
  }

  @action clearLoaderFor = (id) => {
    this.loader.delete(id)
  }

  @action createObjectInRemote = async (inputData: { _id: string } & any, autoPublish = false) => {
    if (!autoPublish && inputData._id) {
      await this.saveOfflineData(inputData)
    }

    inputData._id && this.loader.set(inputData._id, {id: inputData._id})
    const {catalog} = this, {stores: {apollo}, filter, dragDrop, offlineItemsByIndex, index} = catalog,
      localData = inputData._id ? await offlineItemsByIndex[index][inputData._id] : inputData,
      formattedData = (Object.entries(localData)).reduce((result, [key, value]) => {
        if (key !== 'images') return _.setWith(result, key, value, Object)
        else return result
      }, {})
    // TODO add validation here for required fields which is to be taken from databaseConfig according to the index
    let images = []
    if (localData['images']?.length) {
      images = await Promise.all(localData['images'].map(async (image) => {
        if (image.file) {
          let uploadInput: IUpload = {
            entityIds: [],
            files: image,
            isRefetchNeeded: false
          }
          return await catalog.img.prepareUploads(uploadInput)
        } else {
          return image
        }
      }))
    }

    try {
      const variables = {
        input: {
          index,
          data: {
            ...formattedData,
            images: images.filter(_.identity) //_.first(images)
          }
        }
      }
      const {data: {createCatalogItem: data}} = await apollo.client.mutate({
        mutation: CREATE_CATALOG_ITEM,
        variables: variables
      })

      if (_.isString(data)) {
        catalog.focusedCatalogItemId = data
        catalog.newlyCreatedIds.push(data)
        if (catalog.selection) {
          catalog.selection.selectedIds.replace([data])
        }
      } else if (data.length) {
        catalog.newlyCreatedIds.push(...data)
        const createItem = data[0]
        createItem?.exId && (catalog.focusedCatalogItemId = createItem?.exId)
      }
      inputData._id && await catalog?.mutations?.deleteItem(inputData._id)
      catalog.isCatalogStores && await catalog?.fetchWorkspaceCounts()
      filter && await filter.refetch(true)

      return data
    } catch (e) {
      console.log(e)
    } finally {
      inputData._id && this.clearLoaderFor(inputData._id)
    }
  }
}

export interface ICreateItemData {
  __create?: {
    multiple?: boolean
  }
}


// WEBPACK FOOTER //
// ./src/stores/catalog/CatalogCreateItemStore.tsx